/**
 * Гиперболическая метрика и геодезические в модели Пуанкаре (диске).
 *
 * Гиперболическая плоскость представляется открытым единичным диском
 * `D = { z ∈ ℂ : |z| < 1 }`. Расстояние от центра до точки на евклидовом
 * радиусе `r` равно `d = 2·artanh(r) = ln((1+r)/(1−r))`, обратно `r = tanh(d/2)`.
 * Геодезические — диаметры и дуги окружностей, ортогональных границе диска.
 *
 * Все функции чистые и детерминированные; вычисления геодезических выполняются
 * через изометрии из {@link module:mobius} (перенос концов отрезка в центр),
 * что даёт корректную гиперболическую интерполяцию, а не линейную на экране.
 *
 * Требования: 3.5, 4.6, 8.7
 */

import type { Complex } from './map-types.js';
import { fromPolar, modulus, argument } from './complex.js';
import { apply, translateToOrigin, translateFromOrigin } from './mobius.js';

/**
 * Максимальный евклидов радиус, считающийся «внутри» диска.
 * Значения `r ≥ 1` не имеют конечного гиперболического расстояния, поэтому
 * при численных погрешностях радиус зажимается чуть ниже границы.
 */
const MAX_RADIUS = 1 - 1e-15;

/**
 * Преобразование `distance → radius`: евклидов радиус точки, находящейся на
 * гиперболическом расстоянии `d ≥ 0` от центра диска. `r = tanh(d/2)`.
 *
 * Обратно к {@link radiusToDistance}.
 */
export function distanceToRadius(d: number): number {
  return Math.tanh(d / 2);
}

/**
 * Преобразование `radius → distance`: гиперболическое расстояние от центра
 * диска до точки на евклидовом радиусе `r ∈ [0, 1)`.
 * `d = 2·artanh(r) = ln((1+r)/(1−r))`.
 *
 * Радиус зажимается в `[0, MAX_RADIUS]`, чтобы избежать `±∞`/`NaN` у границы.
 * Обратно к {@link distanceToRadius}.
 */
export function radiusToDistance(r: number): number {
  const clamped = Math.min(Math.max(Math.abs(r), 0), MAX_RADIUS);
  return 2 * Math.atanh(clamped);
}

/**
 * Гиперболическое расстояние между двумя точками диска `a` и `b`.
 *
 * Вычисляется переносом `a` в центр диска изометрией {@link translateToOrigin}:
 * тогда расстояние равно расстоянию от центра до образа `b`,
 * `d = 2·artanh(|g(b)|)`, где `g` переводит `a` в `0`.
 */
export function distance(a: Complex, b: Complex): number {
  const toOrigin = translateToOrigin(a);
  const image = apply(toOrigin, b);
  return radiusToDistance(modulus(image));
}

/**
 * Гиперболическая середина геодезического отрезка `[a, b]`.
 *
 * Отрезок переносится изометрией `g = translateToOrigin(a)` так, что `a → 0`,
 * а образ `b` лежит на прямом радиусе. Середина берётся как точка на половине
 * гиперболического расстояния вдоль этого радиуса, после чего переносится
 * обратно `g⁻¹`. Результат лежит на геодезической и равноудалён от `a` и `b`.
 */
export function midpoint(a: Complex, b: Complex): Complex {
  const toOrigin = translateToOrigin(a);
  const image = apply(toOrigin, b);
  const r = modulus(image);
  const phi = argument(image);
  const halfDistance = radiusToDistance(r) / 2;
  const midInOriginFrame = fromPolar(distanceToRadius(halfDistance), phi);
  return apply(translateFromOrigin(a), midInOriginFrame);
}

/**
 * `n` точек, равномерно распределённых по гиперболическому расстоянию вдоль
 * геодезической от `a` до `b` (включая оба конца).
 *
 * Точка `a` переносится в центр, образ `b` оказывается на радиусе под углом
 * `phi` на полном расстоянии `D`; выборка `i` берётся на расстоянии `t·D`
 * (`t = i/(n−1)`) от центра и переносится обратно. Так интерполяция
 * учитывает кривизну, а не идёт линейно в экранных координатах.
 *
 * @param n число точек (`n ≥ 2`). При `n ≤ 1` возвращается `[a]`.
 */
export function geodesicPoints(a: Complex, b: Complex, n: number): Complex[] {
  if (n <= 1) {
    return [{ ...a }];
  }

  const toOrigin = translateToOrigin(a);
  const fromOrigin = translateFromOrigin(a);
  const image = apply(toOrigin, b);
  const phi = argument(image);
  const totalDistance = radiusToDistance(modulus(image));

  const points: Complex[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const radius = distanceToRadius(t * totalDistance);
    const pointInOriginFrame = fromPolar(radius, phi);
    points.push(apply(fromOrigin, pointInOriginFrame));
  }
  return points;
}

/**
 * Ломаная аппроксимация геодезической дуги `[a, b]` для рендеринга.
 *
 * Возвращает `segments + 1` точек (включая концы), соединение которых отрезками
 * приближает истинную геодезическую дугу модели Пуанкаре. Чем больше
 * `segments`, тем меньше ошибка аппроксимации кривой.
 *
 * @param segments число отрезков ломаной (`segments ≥ 1`).
 */
export function geodesicArc(a: Complex, b: Complex, segments: number): Complex[] {
  const count = Math.max(1, Math.floor(segments));
  return geodesicPoints(a, b, count + 1);
}
